"use client";

import { useEffect, useState } from "react";
import { createLogger } from "@/lib/logger";
import { useRouter } from "next/navigation";
import LeaderAvatar from "@/app/components/LeaderAvatar";
import Link from "next/link";

const logger = createLogger("RecentActivity");
import {
  FiTrendingUp,
  FiTrendingDown,
  FiPackage,
  FiCopy,
  FiRefreshCw,
  FiCheckCircle,
  FiShare2
} from "react-icons/fi";
import SharePositionModal from "@/app/components/SharePositionModal";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { sourceUrl, extractKalshiEventTicker, marketPageUrl } from "@/lib/source-url";
import { marketDisplayTitle } from "@/lib/markets/title";
import { useT } from "@/hooks/useT";

interface Trade {
  id: string;
  marketSlug: string;
  eventSlug?: string | null;
  /** Canonical event headline (gamma events[0].title) — preferred title so
   *  Activity matches the event page / Positions. */
  eventTitle?: string | null;
  isMultiOutcome?: boolean;
  marketQuestion: string;
  marketImage: string | null;
  outcome: string;
  outcomeIndex: number;
  /** Polymarket per-sub-market variant ("28°C", "82-83°F") for multi-
   *  outcome events. Used in place of the binary Yes/No outcome chip
   *  when present so the Activity row carries the discriminating
   *  label instead of the meaningless binary. */
  groupItemTitle?: string | null;
  side: "BUY" | "SELL" | null;
  size: number;
  price: number;
  fee: number;
  timestamp: string;
  status: string;
  source: "trade" | "copy" | "polymarket" | "swap" | "redemption" | "kalshi" | "predict" | "hyperliquid";
  feeType: string;
  kalshiTicker?: string;
}

/** Image with cascading fallback URLs — tries each src before showing icon.
 *
 * 2026-05-28: switched from `next/image` to plain `<img>` for activity
 * thumbs. The optimizer adds 200-500 ms per cold-miss (fetch S3 + encode
 * AVIF), and activity rows are mostly historical markets so the miss
 * ratio is high. At 48 px the AVIF savings are 5-10 KB per image while
 * the round-trip to our Railway box dominates. Browser handles caching
 * (heuristic if Polymarket S3 doesn't send headers) and downsampling
 * from the 96/256 px original — both cheap. Net effect: thumbs paint
 * on the first request without paying our optimizer cost.
 */
function ActivityImage({ srcs, alt, fallback }: { srcs: string[]; alt: string; fallback: React.ReactNode }) {
  const [idx, setIdx] = useState(0);
  const validSrcs = srcs.filter(Boolean);
  if (idx >= validSrcs.length) return <>{fallback}</>;
  return (
    <img
      src={validSrcs[idx]}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover"
      onError={() => setIdx(i => i + 1)}
    />
  );
}

function tradeImageSrcs(trade: Trade): string[] {
  const srcs: string[] = [];
  if (trade.marketImage) srcs.push(trade.marketImage);
  return [...new Set(srcs)];
}

/** Source de plataforma de una fila de activity (para el badge / share card).
 *  `source` es el TIPO de evento (trade/copy/redemption); `platform` (cuando
 *  está) es la plataforma real. Default polymarket. */
function activityPlatform(trade: Trade): 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid' {
  const platform = (trade as any).platform || trade.source;
  if (platform === 'hyperliquid') return 'hyperliquid';
  if (platform === 'kalshi') return 'kalshi';
  if (platform === 'predict') return 'predict';
  return 'polymarket';
}

// Estados terminales de un market (resuelto) — espejo de PositionsSection.
const SETTLED_STATUSES = ['resolved', 'settled', 'determined', 'amended', 'finalized', 'disputed'];

/** Resultado de una posición cerrada para el share card (won/lost/sold), o null
 *  si sigue abierta. Misma lógica que PositionsSection.getClosedResult. */
function positionClosedResult(p: { isOpen?: boolean; shares?: number; winningOutcome?: string | null; marketStatus?: string | null; realizedPnL?: number }): 'won' | 'lost' | 'sold' | null {
  // A resolved market is "closed" even when the row is still is_open=true with
  // shares > 0: a settled loser keeps its shares until the auto-redeem worker
  // fires. Without the `resolved` clause that LOST position fell through to the
  // live-card path and rendered as break-even at the entry price. So treat
  // resolved-against/for as closed regardless of is_open/shares.
  const resolved = !!p.winningOutcome || SETTLED_STATUSES.includes(String(p.marketStatus || ''));
  const closed = !p.isOpen || (Number(p.shares) || 0) <= 0 || resolved;
  if (!closed) return null;
  if (resolved) return (Number(p.realizedPnL) || 0) >= 0 ? 'won' : 'lost';
  return 'sold';
}

// Dev-only mock activity rows. Injected when `?mockActivity=1` is set on
// localhost so the column layout can be previewed without trade history.
// NODE_ENV gate keeps prod builds free of this.
function buildMockTrades(): Trade[] {
  const now = Date.now();
  // `over` allows extra keys (pnlUsd, leaderName, …) the row branches read
  // via `(trade as any).x` — they aren't on the Trade interface but the mock
  // must set them to exercise the real render paths (redeem P&L, copy chip).
  // Spread extras bypass excess-property checks at runtime.
  const mk = (i: number, over: Partial<Trade> & Record<string, unknown>): Trade => ({
    id: `mock-act-${i}`,
    marketSlug: `mock-slug-${i}`,
    eventSlug: null,
    isMultiOutcome: false,
    marketQuestion: `Mock activity row ${i + 1} — will something happen on May ${20 + i}?`,
    marketImage: null,
    outcome: i % 2 === 0 ? 'Yes' : 'No',
    outcomeIndex: i % 2 === 0 ? 0 : 1,
    side: 'BUY',
    size: 0,
    price: 0,
    fee: 0,
    timestamp: new Date(now - i * 5 * 60_000).toISOString(),
    status: 'open',
    source: 'polymarket',
    feeType: '',
    ...over,
  });
  // One representative row of every activity branch the renderer handles.
  return [
    mk(0, { side: 'BUY', size: 12.5, price: 0.17, fee: 0.04, status: 'open' }),
    mk(1, { side: 'SELL', size: 8.3, price: 0.34, fee: 0.03, status: 'open' }),
    mk(2, { side: 'BUY', size: 50, price: 0.22, fee: 0.11, status: 'won', outcome: 'No', outcomeIndex: 1 }),
    mk(3, { side: 'BUY', size: 27, price: 0.45, fee: 0.10, status: 'lost' }),
    mk(4, { side: 'BUY', size: 1.5, price: 0.85, fee: 0.01, source: 'copy', status: 'open', leaderName: '0x69820bc1f2de4661ed88a30c99a7a9449aa84a165', leaderImage: null }),
    // Big amount → checks the >9,999 / 2-decimal compact formatting (shares
    // 123,456 → "123.46K", amount $12,345.60 → "$12.35K")
    mk(5, { side: 'BUY', size: 123456, price: 0.1, fee: 12.5, status: 'open', marketQuestion: 'Mock big-ticket buy — does the Amount column overflow?' }),
    // Swap (totalValue = fee)
    mk(6, { source: 'swap', side: null, fee: 25.5, status: 'open' }),
    // Redeem — profitable
    mk(7, { source: 'redemption', side: null, size: 50, price: 1, status: 'won', outcome: 'Yes', outcomeIndex: 0, pnlUsd: 28.4, marketQuestion: 'Mock redeem — resolved YES, in profit' }),
    // Redeem — loss
    mk(8, { source: 'redemption', side: null, size: 30, price: 0, status: 'lost', outcome: 'No', outcomeIndex: 1, pnlUsd: -13.5, marketQuestion: 'Mock redeem — resolved NO, at a loss' }),
  ];
}

// Activity feed pagination — prev/next pages fetched server-side via
// ?limit&offset (mirrors the /copytrading Recent Trades page nav).
const ACTIVITY_PAGE_SIZE = 20;

// Short label for the "via @<leader>" chip — collapse address-shaped
// values (optionally with a "-<leaderId>" suffix) to `0x6982…A165` so a
// raw wallet doesn't overflow the chip. Mirrors PositionsSection.
function shortLeaderName(name?: string | null): string {
  if (!name) return "";
  const addr = name.match(/^(0x[0-9a-fA-F]{4})[0-9a-fA-F]+([0-9a-fA-F]{4})/);
  if (addr) return `${addr[1]}…${addr[2]}`;
  return name.length > 16 ? `${name.slice(0, 14)}…` : name;
}

export default function RecentActivitySection() {
  const t = useT();
  const router = useRouter();
  const { isLoggedIn } = useAuth();
  // Dev-only mock activity. `?mockActivity=1` injects 5 representative
  // rows so the columnar layout is previewable without trade history.
  // NODE_ENV gate keeps prod builds free of this.
  const isMockActivity = (() => {
    if (typeof window === 'undefined') return false;
    if (process.env.NODE_ENV !== 'development') return false;
    return new URLSearchParams(window.location.search).get('mockActivity') === '1';
  })();
  const [trades, setTrades] = useState<Trade[]>(isMockActivity ? buildMockTrades() : []);
  const [loading, setLoading] = useState(!isMockActivity);
  const [error, setError] = useState<string | null>(null);
  // Server-side pagination (offset = page * ACTIVITY_PAGE_SIZE). `hasMore`
  // is inferred from a full page coming back. `refreshKey` lets the
  // realtime refresh listener force a refetch of page 0.
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Datos resueltos para el PnL share card (mismo modal que Positions). Una fila
  // de activity es un FILL suelto (sin P&L/resultado fiables), así que al
  // compartir buscamos la POSICIÓN real del market y compartimos esa — si no
  // existe, caemos a los datos del trade SIN inventar "WON".
  const [shareData, setShareData] = useState<{
    position: { outcome: string; outcomeIndex: number; shares: number; avgPrice: number; currentPrice: number; invested: number; currentValue: number; totalPnL: number };
    marketQuestion: string;
    marketSlug: string;
    marketImage?: string;
    source: 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid';
    closedResult: 'won' | 'lost' | 'sold' | null;
    /** Precio de venta (solo SELL) → pill "entrada → venta" en el card. */
    exitPrice?: number;
  } | null>(null);

  useEffect(() => {
    if (isMockActivity) return; // mock data already seeded, don't fetch
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const fetchTrades = async () => {
      try {
        setLoading(true);
        const offset = page * ACTIVITY_PAGE_SIZE;
        const res = await authFetch(`/api/portfolio/activity?limit=${ACTIVITY_PAGE_SIZE}&offset=${offset}&_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) throw new Error("Failed to fetch trades");

        const data = await res.json();
        const rows: Trade[] = data.trades || [];
        setTrades(rows);
        // A full page back ⇒ there's very likely another page. Short page
        // ⇒ we're at the end. (No total count from the endpoint.)
        setHasMore(rows.length === ACTIVITY_PAGE_SIZE);
      } catch (err) {
        logger.error("Error fetching trades", { error: err instanceof Error ? err.message : String(err) });
        setError(t("portfolio.activity.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchTrades();

    // Listen for real-time trade events — jump back to page 0 so the new
    // trade is visible. setPage(0) refetches via the deps below when we're
    // off page 0; the refreshKey bump forces a refetch when already on it.
    const handleRefresh = () => {
      setPage(0);
      setRefreshKey((k) => k + 1);
    };
    window.addEventListener("portfolio:refresh", handleRefresh);
    return () => window.removeEventListener("portfolio:refresh", handleRefresh);
  }, [isLoggedIn, isMockActivity, page, refreshKey]);

  // Truncate to N decimals without rounding
  const truncateDecimals = (value: number, decimals: number) => {
    const multiplier = Math.pow(10, decimals);
    return Math.trunc(value * multiplier) / multiplier;
  };

  const formatCurrency = (value: number, options?: { maxDecimals?: number; minDecimals?: number; truncate?: boolean }) => {
    const maxDecimals = options?.maxDecimals ?? 3;
    const minDecimals = options?.minDecimals ?? 2;
    // Truncate instead of round when truncate option is true (default)
    const truncatedValue = options?.truncate !== false ? truncateDecimals(value, maxDecimals) : value;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    }).format(truncatedValue);
  };

  // Format share count without noisy decimals.
  // Polymarket returns shares as USD / exact-price → results like
  // 7.857141 or 9.297519 with up to 6 decimal places. None of that
  // precision is useful in a feed (the position card already uses
  // .toFixed(1) for the same field) and it looks broken next to a
  // round trade like "56.5". 2 decimals max keeps integer trades clean
  // (38 stays 38, not 38.00) and rounds fractional ones to readable
  // numbers (7.857141 → 7.86).
  const formatNumber = (value: number, maxDecimals: number = 2) => {
    const formatted = value.toFixed(maxDecimals);
    return parseFloat(formatted).toString();
  };

  // Share count for the feed: trims noisy decimals (formatNumber) and
  // compacts past 9,999 (12,345 → "12.3K", 1,000,000 → "1M") so a whale-
  // sized position doesn't blow the 110px column.
  const formatCount = (value: number) => {
    if (Math.abs(value) >= 10000) {
      return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(value);
    }
    return formatNumber(value);
  };

  // Money for the Amount / Payout / P&L totals. Three rules the user asked
  // for: max 2 decimals, drop trailing ".00"/".x0" zeros ($50.00 → $50,
  // $12.50 → $12.5), and compact past 9,999 ($12,345 → "$12.3K") so the
  // value fits the column. Truncates (never rounds up) like formatCurrency.
  const formatMoney = (value: number) => {
    const v = truncateDecimals(value, 2);
    if (Math.abs(v) >= 10000) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2 }).format(v);
    }
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
  };

  // Format price in cents without rounding
  const formatPriceCents = (price: number) => {
    const cents = price * 100;
    // Show up to 4 decimal places, remove trailing zeros
    return parseFloat(cents.toFixed(4)).toString();
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t("portfolio.activity.justNow");
    if (minutes < 60) return t("portfolio.activity.minutesAgo", { n: minutes });
    if (hours < 24) return t("portfolio.activity.hoursAgo", { n: hours });
    if (days < 7) return t("portfolio.activity.daysAgo", { n: days });

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Abre el share card para una fila de activity. Una fila es un FILL suelto
  // (su precio/notional NO es el avg/invested/P&L de la posición), así que
  // resolvemos la POSICIÓN real del market PRIMERO y abrimos el modal UNA sola
  // vez con el dato final — igual que la pestaña Positions. (Antes abríamos con
  // un fallback del trade y refinábamos al llegar la posición, lo que
  // regeneraba el card dos veces = el "refetchea solo de nuevo", y si el user
  // capturaba el primer render veía datos del trade: invested = notional del
  // fill, P&L $0, layout abierto.) Fallback al trade SOLO si no hay posición.
  const [shareLoadingId, setShareLoadingId] = useState<string | null>(null);
  const openShare = async (trade: Trade) => {
    const platform = activityPlatform(trade);
    const title = marketDisplayTitle(
      { isMultiOutcome: trade.isMultiOutcome, eventTitle: trade.eventTitle, eventSlug: trade.eventSlug, marketQuestion: trade.marketQuestion, groupItemTitle: trade.groupItemTitle, outcomes: (trade as any).outcomes },
      trade.marketQuestion || ''
    );

    // Intenta resolver la posición real (status=closed incluye vendidas/redimidas).
    if (trade.marketSlug && platform !== 'hyperliquid') {
      setShareLoadingId(trade.id);
      try {
        const res = await authFetch(`/api/portfolio/positions?status=closed&includeResolved=1&marketSlug=${encodeURIComponent(trade.marketSlug)}`);
        if (res.ok) {
          const data = await res.json();
          const positions: any[] = data.positions || [];
          const p = positions.find((x) => x.marketSlug === trade.marketSlug && x.outcomeIndex === trade.outcomeIndex)
            || positions.find((x) => x.marketSlug === trade.marketSlug)
            || positions[0];
          if (p) {
            setShareData({
              position: {
                outcome: p.outcome ?? trade.outcome,
                outcomeIndex: p.outcomeIndex ?? trade.outcomeIndex,
                shares: Number(p.shares) || 0,
                avgPrice: Number(p.avgPrice) || 0,
                currentPrice: Number(p.currentPrice) || 0,
                invested: Number(p.invested) || 0,
                currentValue: Number(p.currentValue) || 0,
                totalPnL: Number(p.totalPnL) || 0,
              },
              marketQuestion: title,
              marketSlug: trade.marketSlug,
              marketImage: p.marketImage || trade.marketImage || undefined,
              source: platform,
              closedResult: positionClosedResult(p),
              // Si la fila es un SELL, su precio es el de SALIDA → pill
              // "entrada → venta". (El avg de la posición es la entrada.)
              exitPrice: trade.side === 'SELL' ? trade.price : undefined,
            });
            setShareLoadingId(null);
            return;
          }
        }
      } catch { /* cae al fallback del trade */ }
      setShareLoadingId(null);
    }

    // Fallback: datos del trade (un BUY/SELL suelto NO es "WON" — solo las
    // redenciones tienen resultado fiable a nivel de fila).
    setShareData({
      position: {
        outcome: trade.outcome,
        outcomeIndex: trade.outcomeIndex,
        shares: trade.size,
        avgPrice: trade.price,
        currentPrice: trade.price,
        invested: trade.size * trade.price,
        currentValue: trade.size * trade.price,
        totalPnL: Number((trade as any).pnlUsd ?? 0),
      },
      marketQuestion: title,
      marketSlug: trade.marketSlug,
      marketImage: trade.marketImage || undefined,
      source: platform,
      closedResult: trade.source === 'redemption'
        ? (Number((trade as any).pnlUsd ?? 0) >= 0 ? 'won' : 'lost')
        : null,
    });
  };

  // Botón de compartir (PnL share card) — mismo icono que Positions
  // (FiShare2). stopPropagation para no disparar la navegación de la fila.
  const shareButton = (trade: Trade) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openShare(trade); }}
      disabled={shareLoadingId === trade.id}
      className="flex-shrink-0 p-1 rounded-[4px] text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white transition-colors disabled:opacity-60"
      title={t("portfolio.positions.share")}
      aria-label={t("portfolio.positions.share")}
    >
      {shareLoadingId === trade.id
        ? <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />
        : <FiShare2 className="w-3.5 h-3.5" />}
    </button>
  );

  // Only show the full skeleton on the FIRST load (empty list). On page
  // navigation we keep the current rows visible until the next page lands,
  // so the prev/next controls don't flash out and back.
  if (loading && trades.length === 0) {
    return (
      <div className="space-y-3">
        {/* Loading header */}
        <div className="flex items-center gap-2 px-1">
          <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin" />
          <span className="text-xs text-gray-500 dark:text-gray-400">{t("portfolio.activity.loading")}</span>
        </div>
        {/* Skeleton cards */}
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A]" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 skeleton-shimmer rounded-[5px]" />
              <div className="flex-1">
                <div className="h-3.5 w-2/3 skeleton-shimmer rounded mb-1.5" />
                <div className="h-3 w-1/3 skeleton-shimmer rounded" />
              </div>
              <div className="w-16 h-6 skeleton-shimmer rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-[5px]">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-6 text-center border border-gray-200 dark:border-[#1A1A1A] transition-colors">
        <div className="w-12 h-12 mx-auto mb-3 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
          <FiPackage className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="font-monument text-base text-gray-900 dark:text-white mb-1">{t("portfolio.activity.empty")}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t("portfolio.activity.emptySubtitle")}</p>
        <Link
          href="/"
          className="px-4 py-2 rounded-[5px] bg-[#CCFF00] text-[#0A0A0A] font-satoshi font-medium text-sm hover:bg-[#CCFF00]/80 transition-colors active:scale-[0.98]"
        >
          {t("portfolio.activity.startTrading")}
        </Link>
      </div>
    );
  }

  return (
    // @container so the grid/stacked toggle below keys off the ACTUAL
    // available width (the portfolio right column), not the viewport.
    // The 7-col grid needs ~640px; below that it overflowed and badges/
    // header labels overlapped when the app sidebar was expanded
    // (right column shrinks to ~360-510px). Container query swaps to the
    // stacked card layout instead. 2026-05-28.
    <div className="@container space-y-2">
      {/* Desktop column header — same grid template as each trade row
          below so the labels sit exactly above their values. Hidden on
          narrow containers (rows stack instead). */}
      <div
        className="hidden @[640px]:grid px-3 py-2 gap-3 items-center text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400"
        style={{ gridTemplateColumns: '40px minmax(0, 1fr) 90px 110px 90px 80px 70px 36px' }}
      >
        <span />
        <span>{t("portfolio.activity.col.market")}</span>
        <span className="text-center">{t("portfolio.activity.col.side")}</span>
        <span className="text-center">{t("portfolio.activity.col.shares")}</span>
        <span className="text-center">{t("portfolio.activity.col.notional")}</span>
        <span className="text-center">{t("portfolio.activity.col.status")}</span>
        <span className="text-center">{t("portfolio.activity.col.time")}</span>
        <span />
      </div>
      {trades.map((trade, index) => {
        const isSwap = trade.source === "swap";
        const isBuy = trade.side === "BUY";
        const isYes = trade.outcomeIndex === 0;
        const totalValue = isSwap ? trade.fee : trade.size * trade.price;
        const isCopyTrade = trade.source === "copy";
        // platform is the per-row badge tag (K/P/PM) — separate from `source`
        // which now tags 'redemption' for any redeem regardless of platform.
        // Fallback to source for rows that don't carry the new field yet.
        const platform = (trade as any).platform || trade.source;
        const isKalshi = platform === "kalshi";
        const isPredict = platform === "predict";
        const isHyperliquid = platform === "hyperliquid";
        const isWon = trade.status === "won";
        const isLost = trade.status === "lost";
        const isResolving = trade.status === "resolving";
        const isResolvedTrade = isWon || isLost || isResolving;
        const amountToneClass = isBuy && !isResolvedTrade
          ? "text-[#FF1744]"
          : !isBuy
          ? "text-[#00FF9D]"
          : "text-gray-900 dark:text-white";
        const amountPrefix = isBuy && !isResolvedTrade ? "-" : !isBuy ? "+" : "";

        const statusBadge = isWon
          ? {
              label: t("portfolio.activity.won"),
              className: "bg-[#00FF9D]/20 text-[#00FF9D]",
              icon: <FiCheckCircle className="w-2.5 h-2.5" />,
            }
          : isLost
          ? {
              label: t("portfolio.activity.lost"),
              className: "bg-[#FF1744]/20 text-[#FF1744]",
              icon: <FiTrendingDown className="w-2.5 h-2.5" />,
            }
          : isResolving
          ? {
              label: t("portfolio.activity.resolving"),
              className: "bg-blue-500/20 text-blue-400",
              icon: <FiRefreshCw className="w-2.5 h-2.5" />,
            }
          : {
              // Anything not terminal (won/lost/resolving) = the market is
              // still trading → position is live. Fallback rather than gating
              // on status === "open", since the feed's non-terminal value
              // isn't always literally "open".
              label: t("portfolio.activity.active"),
              className: "bg-indigo-500/10 text-indigo-500 dark:text-indigo-400",
              icon: null as React.ReactNode,
            };

        // Route to correct market page based on source — for PM prefer event page for multi-outcome markets
        const marketHref = isHyperliquid
          ? (trade.marketSlug ? `/hyperliquid/event/${trade.marketSlug}` : null)
          : isPredict
          ? (trade.eventSlug ? `/predictfun/event/${trade.eventSlug}` : `/predictfun/market/${trade.marketSlug}`)
          : isKalshi
          ? sourceUrl(`/event/kalshi/${extractKalshiEventTicker(trade.kalshiTicker || trade.marketSlug)}`, "kalshi")
          : trade.marketSlug ? marketPageUrl({ marketSlug: trade.marketSlug, eventSlug: trade.eventSlug, outcome: trade.outcome, source: 'polymarket', isMultiOutcome: trade.isMultiOutcome }) : null;

        // Render swap activities — same grid (desktop) + stacked (mobile)
        // shape as the other rows. A swap is just a currency conversion, so
        // Side / Shares / Status are dashed; the converted amount goes in the
        // Amount column (neutral colour — it's neither a gain nor a loss).
        if (isSwap) {
          const SwapIcon = ({ className = "" }: { className?: string }) => (
            <div className={`w-10 h-10 rounded-[5px] flex items-center justify-center flex-shrink-0 bg-blue-500/20 ${className}`}>
              <FiRefreshCw className="w-5 h-5 text-blue-400" />
            </div>
          );
          const swapBadge = (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-0.5 bg-blue-500/20 text-blue-400">
              <FiRefreshCw className="w-2.5 h-2.5" />{t("portfolio.activity.swap")}
            </span>
          );
          const swapAmountEl = trade.fee > 0 ? (
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatMoney(trade.fee)}</p>
          ) : (
            <span className="text-[10px] text-gray-400 dark:text-gray-500">—</span>
          );
          return (
            <div
              key={trade.id || index}
              className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors"
            >
              {/* Desktop grid */}
              <div className="hidden @[640px]:grid p-3 items-center gap-3" style={{ gridTemplateColumns: '40px minmax(0, 1fr) 90px 110px 90px 80px 70px 36px' }}>
                <SwapIcon />
                <div className="min-w-0">
                  <h3 className="text-xs text-gray-900 dark:text-white line-clamp-1">USDC → USDC.e</h3>
                  <div className="mt-1">{swapBadge}</div>
                </div>
                <div className="flex justify-center"><span className="text-[10px] text-gray-400 dark:text-gray-500">—</span></div>
                <div className="flex justify-center"><span className="text-[10px] text-gray-400 dark:text-gray-500">—</span></div>
                <div className="text-center tabular-nums">{swapAmountEl}</div>
                <div className="flex justify-center"><span className="text-[10px] text-gray-400 dark:text-gray-500">—</span></div>
                <div className="text-center text-[10px] text-gray-500 dark:text-gray-400">{formatTime(trade.timestamp)}</div>
                <div className="flex justify-center"><span className="text-[10px] text-gray-400 dark:text-gray-500">—</span></div>
              </div>

              {/* Mobile stacked */}
              <div className="@[640px]:hidden p-3">
                <div className="flex items-start gap-3 mb-2">
                  <SwapIcon />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-gray-900 dark:text-white line-clamp-1 mb-1">USDC → USDC.e</h3>
                    <div className="flex items-center gap-1.5 flex-wrap">{swapBadge}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pl-[52px]">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{formatTime(trade.timestamp)}</span>
                  {trade.fee > 0 && (
                    <p className="font-monument text-sm text-gray-900 dark:text-white">{formatMoney(trade.fee)}</p>
                  )}
                </div>
              </div>
            </div>
          );
        }

        // Platform icon — bottom-right normally, bottom-left when copytrade badge takes bottom-right
        const activitySource = isHyperliquid ? 'hyperliquid' : isKalshi ? 'kalshi' : isPredict ? 'predict' : 'polymarket';

        // Render redemption activities
        const isRedemption = trade.source === "redemption";
        if (isRedemption) {
          // P&L comes from the backend; payout = shares × resolution price
          // ($1 if the held outcome won, $0 if it lost).
          const pnl = (trade as any).pnlUsd ?? (trade.size * (1 - trade.price));
          const isProfitable = pnl >= 0;
          const payout = trade.size * (trade.price || (isProfitable ? 1 : 0));
          // Redeem outcome is terminal by definition → WON/LOST from the P&L
          // sign (not trade.status, which isn't always set on redeem rows).
          const redeemStatus = isProfitable
            ? { label: t("portfolio.activity.won"), className: "bg-[#00FF9D]/20 text-[#00FF9D]", icon: <FiCheckCircle className="w-2.5 h-2.5" /> }
            : { label: t("portfolio.activity.lost"), className: "bg-[#FF1744]/20 text-[#FF1744]", icon: <FiTrendingDown className="w-2.5 h-2.5" /> };
          const redeemTitle = marketDisplayTitle({ isMultiOutcome: trade.isMultiOutcome, eventTitle: trade.eventTitle, eventSlug: trade.eventSlug, marketQuestion: trade.marketQuestion, groupItemTitle: trade.groupItemTitle, outcomes: (trade as any).outcomes }, t("portfolio.activity.positionResolved"));

          // Image with the checkmark fallback + platform badge — same 40px
          // cell as trade rows. Defined once, used in both layouts.
          const RedeemImage = ({ className = "" }: { className?: string }) => (
            <div className={`relative w-10 h-10 flex-shrink-0 ${className}`}>
              <div className="w-full h-full rounded-[5px] overflow-hidden">
                {trade.marketImage ? (
                  <ActivityImage srcs={tradeImageSrcs(trade)} alt={trade.marketQuestion || "Market"} fallback={<div className="w-full h-full flex items-center justify-center bg-[#00FF9D]/20"><FiCheckCircle className="w-5 h-5 text-[#00FF9D]" /></div>} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#00FF9D]/20"><FiCheckCircle className="w-5 h-5 text-[#00FF9D]" /></div>
                )}
              </div>
              <div className={`absolute w-4 h-4 rounded-[3px] flex items-center justify-center z-10 -bottom-1 -right-1 ${
                activitySource === 'hyperliquid' ? 'bg-[#97FCE4] text-[8px] font-bold text-black'
                : activitySource === 'kalshi' ? 'bg-emerald-500 text-[8px] font-bold text-white'
                : activitySource === 'predict' ? 'bg-[#8B5CF6] text-[8px] font-bold text-white'
                : 'bg-[#3B82F6]'
              }`}>
                {activitySource === 'hyperliquid' ? 'H'
                  : activitySource === 'kalshi' ? 'K'
                  : activitySource === 'predict' ? 'P'
                  : (<img src="/logos/polymarket/icon-white.svg" alt="PM" className="w-2.5 h-2.5" />)}
              </div>
            </div>
          );
          const redeemBadge = (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-0.5 ${isProfitable ? "bg-[#00FF9D]/20 text-[#00FF9D]" : "bg-[#FF1744]/20 text-[#FF1744]"}`}>
              <FiCheckCircle className="w-2.5 h-2.5" />{t("portfolio.activity.redeem")}
            </span>
          );
          const payoutEl = (
            <p title={`${t("portfolio.activity.payout")}: ${formatCurrency(payout, { maxDecimals: 2 })}`} className={`text-sm font-semibold ${isProfitable ? "text-[#00FF9D]" : "text-[#FF1744]"}`}>{isProfitable ? "+" : ""}{formatMoney(payout)}</p>
          );

          return (
            <div
              key={trade.id || index}
              className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors cursor-pointer"
              onClick={() => trade.marketSlug ? router.push(marketPageUrl({ marketSlug: trade.marketSlug, eventSlug: trade.eventSlug, outcome: trade.outcome, source: activitySource, isMultiOutcome: trade.isMultiOutcome })) : null}
            >
              {/* Desktop grid — same 7-col template as trade rows so values
                  align under the section header. Side + Shares are dashed (a
                  redeem has no buy/sell or fill price); the payout + WON/LOST
                  carry the whole story. */}
              <div className="hidden @[640px]:grid p-3 items-center gap-3" style={{ gridTemplateColumns: '40px minmax(0, 1fr) 90px 110px 90px 80px 70px 36px' }}>
                <RedeemImage />
                <div className="min-w-0">
                  <h3 title={trade.marketQuestion || undefined} className="text-xs text-gray-900 dark:text-white line-clamp-1">{redeemTitle}</h3>
                  <div className="mt-1">{redeemBadge}</div>
                </div>
                {/* Side + Shares don't apply to a redeem (no buy/sell, no fill
                    price) — dashed to keep the row aligned with trade rows. */}
                <div className="flex justify-center"><span className="text-[10px] text-gray-400 dark:text-gray-500">—</span></div>
                <div className="flex justify-center"><span className="text-[10px] text-gray-400 dark:text-gray-500">—</span></div>
                <div className="text-center tabular-nums">{payoutEl}</div>
                <div className="flex justify-center">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-0.5 ${redeemStatus.className}`}>{redeemStatus.icon}{redeemStatus.label}</span>
                </div>
                <div className="text-center text-[10px] text-gray-500 dark:text-gray-400">{formatTime(trade.timestamp)}</div>
                <div className="flex justify-center">{shareButton(trade)}</div>
              </div>

              {/* Mobile stacked — mirrors trade rows */}
              <div className="@[640px]:hidden p-3">
                <div className="flex items-start gap-3 mb-2">
                  <RedeemImage />
                  <div className="flex-1 min-w-0">
                    <h3 title={trade.marketQuestion || undefined} className="text-sm text-gray-900 dark:text-white line-clamp-2 leading-tight mb-1">{redeemTitle}</h3>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {redeemBadge}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pl-[52px]">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><span>{formatTime(trade.timestamp)}</span>{shareButton(trade)}</div>
                  <div className="flex flex-col items-end gap-1">
                    {payoutEl}
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium inline-flex items-center gap-0.5 ${redeemStatus.className}`}>{redeemStatus.icon}{redeemStatus.label}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        const PlatformBadge = () => (
          <div className={`absolute w-4 h-4 rounded-[3px] flex items-center justify-center z-10 ${isCopyTrade ? '-bottom-1 -left-1' : '-bottom-1 -right-1'} ${
            activitySource === 'hyperliquid' ? 'bg-[#97FCE4] text-[8px] font-bold text-black'
            : activitySource === 'kalshi' ? 'bg-emerald-500 text-[8px] font-bold text-white'
            : activitySource === 'predict' ? 'bg-[#8B5CF6] text-[8px] font-bold text-white'
            : 'bg-[#3B82F6]'
          }`}>
            {activitySource === 'hyperliquid' ? 'H'
              : activitySource === 'kalshi' ? 'K'
              : activitySource === 'predict' ? 'P'
              : (<img src="/logos/polymarket/icon-white.svg" alt="PM" className="w-2.5 h-2.5" />)}
          </div>
        );

        const fallbackIcon = (
          <div className={`w-full h-full flex items-center justify-center ${isBuy ? "bg-emerald-500/20" : "bg-red-400/20"}`}>
            {isBuy ? <FiTrendingUp className="w-5 h-5 text-emerald-500" /> : <FiTrendingDown className="w-5 h-5 text-red-400" />}
          </div>
        );
        const TradeImage = ({ className = "" }: { className?: string }) => (
          <div className={`relative w-10 h-10 flex-shrink-0 ${className}`}>
            <div className="w-full h-full rounded-[5px] overflow-hidden">
              {trade.marketImage ? (
                <ActivityImage srcs={tradeImageSrcs(trade)} alt={trade.marketQuestion || "Market"} fallback={fallbackIcon} />
              ) : fallbackIcon}
            </div>
            <PlatformBadge />
            {isCopyTrade && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-[3px] bg-violet-500 flex items-center justify-center">
                <FiCopy className="w-2 h-2 text-white" />
              </div>
            )}
          </div>
        );

        return (
          <div
            key={trade.id || index}
            className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors cursor-pointer"
            onClick={() => marketHref ? router.push(marketHref) : null}
          >
            {/* Desktop Layout — column grid (2026-05-26). Same shape
                as Recent Trades on /copytrading: Market | Side | Shares
                @ Price | Notional | Status | Time. Header sits outside
                the list (rendered at section level) so its labels line
                up above every row's values. */}
            <div
              className="hidden @[640px]:grid p-3 items-center gap-3"
              style={{ gridTemplateColumns: '40px minmax(0, 1fr) 90px 110px 90px 80px 70px 36px' }}
            >
              <TradeImage />
              {/* Col 2: Market title + COPY chip if applicable */}
              <div className="min-w-0">
                <h3 title={trade.marketQuestion || undefined} className="text-xs text-gray-900 dark:text-white line-clamp-1">
                  {marketDisplayTitle({ isMultiOutcome: trade.isMultiOutcome, eventTitle: trade.eventTitle, eventSlug: trade.eventSlug, marketQuestion: trade.marketQuestion, groupItemTitle: trade.groupItemTitle, outcomes: (trade as any).outcomes }, t("portfolio.activity.unknownMarket"))}
                </h3>
                {isCopyTrade && (
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap min-w-0">
                    {(trade as any).leaderName && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#25252B] min-w-0 max-w-full">
                        <span className="opacity-60 flex-shrink-0">{t("ct.exec.viaLabel")}</span>
                        <LeaderAvatar
                          src={(trade as any).leaderImage}
                          address={(trade as any).leaderName}
                          size={12}
                        />
                        <span className="font-medium truncate min-w-0" title={(trade as any).leaderName || undefined}>@{shortLeaderName((trade as any).leaderName)}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Col 3: Side (BUY/SELL) + Outcome (Yes/No) chips stacked */}
              <div className="flex flex-col items-center gap-1 min-w-0 w-full">
                {trade.side && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${isBuy ? "bg-[#00FF9D]/20 text-[#00FF9D]" : "bg-[#FF1744]/20 text-[#FF1744]"}`}>
                    {isBuy ? t("portfolio.activity.buy") : t("portfolio.activity.sell")}
                  </span>
                )}
                {/* 2026-05-26: prefer the multi-outcome variant chip
                    (group_item_title — "28°C", "82-83°F") over the
                    binary Yes/No outcome, which is identical across
                    every sub-market of a multi-outcome event.
                    2026-06-03: sports outcomes ("Map Handicap: HERO
                    (-1.5) vs …") run 50+ chars and blew the 90px column
                    to 4 lines — clamp to 2 lines + full text on hover. */}
                {trade.isMultiOutcome && trade.groupItemTitle ? (
                  <span title={trade.groupItemTitle} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 dark:bg-[#25252B] text-gray-700 dark:text-gray-300 max-w-full line-clamp-2 break-words text-center">
                    {trade.groupItemTitle}
                  </span>
                ) : (
                  <span title={trade.outcome || undefined} className={`px-1.5 py-0.5 rounded text-[10px] font-medium max-w-full line-clamp-2 break-words text-center ${isYes ? "bg-[#00FF9D]/10 text-[#00FF9D]" : "bg-[#FF1744]/10 text-[#FF1744]"}`}>
                    {trade.outcome || (isYes ? t("portfolio.activity.yes") : t("portfolio.activity.no"))}
                  </span>
                )}
              </div>
              {/* Col 4: Shares @ Price */}
              <div className="text-center tabular-nums">
                <p className="text-xs text-gray-900 dark:text-white">
                  {formatCount(trade.size)}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">
                  @ {formatPriceCents(trade.price)}¢
                </p>
              </div>
              {/* Col 5: Amount — cash in/out for this trade. The +/- sign and
                  colour (red out / green in) carry the direction. */}
              <div className="text-center tabular-nums">
                <p title={formatCurrency(totalValue, { maxDecimals: 2 })} className={`text-sm font-semibold ${amountToneClass}`}>
                  {amountPrefix}{formatMoney(totalValue)}
                </p>
              </div>
              {/* Col 6: Status badge (WON/LOST/RESOLVING) — empty when
                  no terminal status yet */}
              <div className="flex justify-center">
                {statusBadge ? (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-0.5 ${statusBadge.className}`}>
                    {statusBadge.icon}{statusBadge.label}
                  </span>
                ) : (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">—</span>
                )}
              </div>
              {/* Col 7: Time */}
              <div className="text-center text-[10px] text-gray-500 dark:text-gray-400">
                {formatTime(trade.timestamp)}
              </div>
              {/* Col 8: Share */}
              <div className="flex justify-center">{shareButton(trade)}</div>
            </div>

            {/* Mobile Layout */}
            <div className="@[640px]:hidden p-3">
              {/* Mobile card (Option 1, 2026-06-09): image spans on the left.
                  Line 1 = title (1 line, truncates) + PnL on the right (the
                  headline number). Line 2 = BUY · outcome · size@price on the
                  left + status · time on the right. */}
              <div className="flex items-start gap-3">
                <TradeImage />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 title={trade.marketQuestion || undefined} className="flex-1 min-w-0 text-sm text-gray-900 dark:text-white truncate leading-tight">{marketDisplayTitle({ isMultiOutcome: trade.isMultiOutcome, eventTitle: trade.eventTitle, eventSlug: trade.eventSlug, marketQuestion: trade.marketQuestion, groupItemTitle: trade.groupItemTitle, outcomes: (trade as any).outcomes }, t("portfolio.activity.unknownMarket"))}</h3>
                    <p title={formatCurrency(totalValue, { maxDecimals: 2 })} className={`flex-shrink-0 font-monument text-sm ${amountToneClass}`}>
                      {amountPrefix}{formatMoney(totalValue)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 min-w-0">
                    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold ${isBuy ? "bg-[#00FF9D]/20 text-[#00FF9D]" : "bg-[#FF1744]/20 text-[#FF1744]"}`}>
                      {isBuy ? t("portfolio.activity.buy") : t("portfolio.activity.sell")}
                    </span>
                    <span title={trade.outcome || undefined} className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-medium max-w-[80px] truncate ${isYes ? "bg-[#00FF9D]/10 text-[#00FF9D]" : "bg-[#FF1744]/10 text-[#FF1744]"}`}>
                      {trade.outcome || (isYes ? t("portfolio.activity.yes") : t("portfolio.activity.no"))}
                    </span>
                    <span className="min-w-0 truncate text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                      {formatCount(trade.size)} @ {formatPriceCents(trade.price)}¢
                    </span>
                    <span className="ml-auto flex-shrink-0 flex items-center gap-1.5">
                      {statusBadge && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium inline-flex items-center gap-0.5 ${statusBadge.className}`}>
                          {statusBadge.icon}{statusBadge.label}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">{formatTime(trade.timestamp)}</span>
                      {shareButton(trade)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {/* Prev/next pagination — mirrors /copytrading Recent Trades. Only
          shown once there's more than one page worth of activity. */}
      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between px-1 pt-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="px-3 py-1.5 rounded-[5px] text-[12px] font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#0f0f12] hover:bg-gray-200 dark:hover:bg-[#151518] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← {t("ct.pagination.prev")}
          </button>
          <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
            {t("ct.pagination.pageOf", { page: page + 1, total: hasMore ? `${page + 1}+` : String(page + 1) })}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore || loading}
            className="px-3 py-1.5 rounded-[5px] text-[12px] font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#0f0f12] hover:bg-gray-200 dark:hover:bg-[#151518] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("ct.pagination.next")} →
          </button>
        </div>
      )}

      {/* Share card — mismo modal que Positions. shareData ya trae la POSICIÓN
          real del market (avg/current/P&L/resultado correctos), resuelta en
          openShare; el fallback del trade no inventa "WON". */}
      {shareData && (
        <SharePositionModal
          isOpen={!!shareData}
          onClose={() => setShareData(null)}
          position={shareData.position}
          marketQuestion={shareData.marketQuestion}
          marketSlug={shareData.marketSlug}
          marketImage={shareData.marketImage}
          source={shareData.source}
          closedResult={shareData.closedResult}
          exitPrice={shareData.exitPrice}
        />
      )}
    </div>
  );
}
